import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { CheckCircle, XCircle, Warning, CaretDown, MagnifyingGlass, X, Copy, Check, FileArrowUp } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";

export default function Travas() {
  const { user } = useAuth();
  const [travas, setTravas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelModal, setCancelModal] = useState(null); // { codigo, motivo }
  const [expandedCard, setExpandedCard] = useState(null);
  const isHedge = user?.role === "hedge" || user?.role === "admin";

  const [filtroData, setFiltroData] = useState("todas");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [filtroVendedor, setFiltroVendedor] = useState("todos");
  const [busca, setBusca] = useState("");
  const buscaRef = useRef("");
  const [totalGeral, setTotalGeral] = useState(0);
  const [copiedCodigo, setCopiedCodigo] = useState(null);
  const handleCopyCodigo = (codigo) => {
    navigator.clipboard.writeText(codigo).then(() => {
      setCopiedCodigo(codigo);
      setTimeout(() => setCopiedCodigo(null), 1500);
    });
  };

  const [csvModal, setCsvModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [vincularModal, setVincularModal] = useState(null); // { codigo, numero_pedido }
  const [vincularLoading, setVincularLoading] = useState(false);
  const [ptaxModal, setPtaxModal] = useState(null); // { codigo, taxa }
  const [ptaxLoading, setPtaxLoading] = useState(false);
  const [confirmCancelModal, setConfirmCancelModal] = useState(null); // { codigo, cliente, motivo_cancelamento, tipo: "aprovar"|"rejeitar" }

  const handleConfirmPtax = async () => {
    const taxaStr = ptaxModal?.taxa?.replace(",", ".") ?? "";
    const taxa = parseFloat(taxaStr);
    if (!taxaStr || isNaN(taxa) || taxa <= 0) {
      toast.error("Informe uma taxa válida");
      return;
    }
    setPtaxLoading(true);
    try {
      await api.put(`/travas/${ptaxModal.codigo}/taxa-ptax`, { taxa });
      toast.success(`Taxa PTAX lançada para trava ${ptaxModal.codigo}`);
      setPtaxModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao lançar taxa");
    } finally {
      setPtaxLoading(false);
    }
  };

  const handleConfirmVincular = async () => {
    if (!vincularModal?.numero_pedido?.trim()) {
      toast.error("Informe o número do pedido");
      return;
    }
    setVincularLoading(true);
    try {
      await api.post(`/travas/${vincularModal.codigo}/vincular`, { numero_pedido: vincularModal.numero_pedido.trim() });
      toast.success(`Trava ${vincularModal.codigo} vinculada ao pedido ${vincularModal.numero_pedido.trim()}`);
      setVincularModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao vincular trava");
    } finally {
      setVincularLoading(false);
    }
  };

  const handleVincularCsv = async () => {
    if (!csvFile) { toast.error("Selecione um arquivo CSV"); return; }
    setCsvLoading(true);
    try {
      const fd = new FormData();
      fd.append("arquivo", csvFile);
      const { data } = await api.post("/travas/vincular-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCsvResult(data);
      if (data.vinculadas > 0 || data.taxa_atualizada > 0) load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao processar CSV");
    } finally {
      setCsvLoading(false);
    }
  };

  const load = async (termo = buscaRef.current) => {
    try {
      const params = termo.trim() ? { search: termo.trim() } : {};
      const { data, headers } = await api.get("/travas", { params });
      setTravas(data);
      const total = Number(headers["x-total-count"]);
      setTotalGeral(Number.isFinite(total) ? total : data.length);
    } catch (e) {
      toast.error("Erro ao carregar travas");
    } finally {
      setLoading(false);
    }
  };

  // Busca aplicada no backend (ILIKE antes do LIMIT), com debounce.
  useEffect(() => {
    const atraso = busca === buscaRef.current ? 0 : 400;
    const timer = setTimeout(() => {
      buscaRef.current = busca;
      load(busca);
    }, atraso);
    return () => clearTimeout(timer);
  }, [busca]);

  const handleConfirmCancelar = async () => {
    if (!cancelModal?.motivo?.trim()) {
      toast.error("Informe o motivo do cancelamento");
      return;
    }
    try {
      await api.post(`/travas/${cancelModal.codigo}/solicitar-cancelamento`, { motivo: cancelModal.motivo });
      toast.success("Solicitação enviada para o Hedge.");
      setCancelModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const aprovar = async (codigo, tipo) => {
    try {
      await api.post(`/travas/${codigo}/${tipo === "cancel" ? "aprovar-cancelamento" : "aprovar-alcada"}`);
      toast.success("Aprovado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail);
    }
  };

  const rejeitar = async (codigo, tipo) => {
    try {
      await api.post(`/travas/${codigo}/${tipo === "cancel" ? "rejeitar-cancelamento" : "rejeitar-alcada"}`);
      toast.success("Rejeitado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail);
    }
  };

  const diasAberto = (iso) => {
    const d = new Date(iso);
    return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  };

  const corDia = (dias) => dias >= 5 ? "text-[#FF3B30]" : dias >= 3 ? "text-[#FFB800]" : "text-[#40B6A2]";

  const fmt = (n, dec = 4) => n?.toLocaleString("pt-BR", { minimumFractionDigits: dec, maximumFractionDigits: dec });

  const acoes = { setCancelModal, aprovar, rejeitar, setVincularModal, setPtaxModal, setConfirmCancelModal };

  const vendedores = [...new Set(travas.map((t) => t.vendedor_nome).filter(Boolean))].sort();

  const travasFiltradas = travas.filter((t) => {
    if (filtroData !== "todas") {
      const dataBRT = new Date(t.data_trava).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
      const hoje = new Date().toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
      if (filtroData === "hoje" && dataBRT !== hoje) return false;
      if (filtroData === "ontem") {
        const ontem = new Date(Date.now() - 86400000).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
        if (dataBRT !== ontem) return false;
      }
      if (filtroData === "7dias" && new Date(t.data_trava).getTime() < Date.now() - 7 * 86400000) return false;
      if (filtroData === "30dias" && new Date(t.data_trava).getTime() < Date.now() - 30 * 86400000) return false;
    }
    if (filtroStatus !== "todos" && t.status !== filtroStatus) return false;
    if (filtroVendedor !== "todos" && t.vendedor_nome !== filtroVendedor) return false;
    return true;
  });

  const filtrosAtivos = filtroData !== "todas" || filtroStatus !== "todos" || filtroVendedor !== "todos" || busca !== "";
  const limparFiltros = () => { setFiltroData("todas"); setFiltroStatus("todos"); setFiltroVendedor("todos"); setBusca(""); };

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-6">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-[11px] tracking-[0.25em] text-[#6b89a8] uppercase">Travas</p>
            <h1 className="text-3xl font-bold tracking-tight">Travas & Vinculação</h1>
          </div>
          <div className="flex items-center gap-3">
            {isHedge && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setCsvModal(true); setCsvFile(null); setCsvResult(null); }}
                className="border-[#123a5e] text-[#8aa9c9] hover:text-white gap-1.5"
              >
                <FileArrowUp className="w-4 h-4" />
                Importar CSV Sysfértil
              </Button>
            )}
            <p className="text-xs text-[#6b89a8] font-mono-data">
              {travasFiltradas.length} trava{travasFiltradas.length !== 1 ? "s" : ""}
              {totalGeral > travasFiltradas.length && (
                <span className="text-[#6b89a8]/60"> · {totalGeral} total</span>
              )}
            </p>
          </div>
        </div>

        {loading ? (
          <p className="text-[#6b89a8] text-sm">Carregando...</p>
        ) : (
          <>
            {/* Barra de filtros */}
            <div className="border border-[#123a5e] rounded-sm bg-[#0B2D4D] p-3 flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#6b89a8]" />
                <input
                  type="text"
                  placeholder="Buscar por código, cliente ou vendedor..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  data-testid="filtro-busca"
                  className="w-full bg-[#061729] border border-[#123a5e] pl-8 pr-8 py-1.5 text-sm text-white placeholder-[#6b89a8] focus:outline-none focus:border-[#40B6A2]/50 rounded-sm"
                />
                {busca && (
                  <button onClick={() => setBusca("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#6b89a8] hover:text-white">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <FilterSelect value={filtroData} onChange={setFiltroData} testid="filtro-data" options={[
                { value: "todas", label: "Todas as datas" },
                { value: "hoje", label: "Hoje" },
                { value: "ontem", label: "Ontem" },
                { value: "7dias", label: "Últimos 7 dias" },
                { value: "30dias", label: "Últimos 30 dias" },
              ]} />
              <FilterSelect value={filtroStatus} onChange={setFiltroStatus} testid="filtro-status" options={[
                { value: "todos", label: "Todos os status" },
                { value: "pendente_vinculacao", label: "Pendente" },
                { value: "vinculada", label: "Vinculada" },
                { value: "cancelada", label: "Cancelada" },
                { value: "rejeitada", label: "Rejeitada" },
                { value: "solicitacao_cancelamento", label: "Canc. Pend." },
                { value: "pendente_aprovacao_alcada", label: "Alçada" },
              ]} />
              {isHedge && (
                <FilterSelect value={filtroVendedor} onChange={setFiltroVendedor} testid="filtro-vendedor" options={[
                  { value: "todos", label: "Todos os vendedores" },
                  ...vendedores.map((v) => ({ value: v, label: v })),
                ]} />
              )}
              {filtrosAtivos && (
                <button
                  onClick={limparFiltros}
                  data-testid="btn-limpar-filtros"
                  className="text-[10px] tracking-[0.15em] uppercase px-3 py-1.5 border border-[#1a4773] text-[#6b89a8] hover:border-[#FF3B30]/50 hover:text-[#FF3B30] rounded-sm transition-colors whitespace-nowrap"
                >
                  Limpar filtros
                </button>
              )}
            </div>

            {travasFiltradas.length === 0 ? (
              <div className="border border-[#123a5e] rounded-sm p-10 text-center text-[#6b89a8] text-sm">
                {travas.length === 0 ? "Nenhuma trava encontrada" : "Nenhuma trava corresponde aos filtros aplicados"}
              </div>
            ) : (
            <>
            {/* Desktop: tabela */}
            <div className="hidden md:block border border-[#123a5e] rounded-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0B2D4D] border-b border-[#123a5e]">
                  <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-[#6b89a8]">
                    <th className="p-3">Código</th>
                    <th className="p-3">Cliente</th>
                    <th className="p-3 text-right">Volume USD</th>
                    <th className="p-3 text-right">Taxa</th>
                    {isHedge && <th className="p-3">Juros</th>}
                    {isHedge && <th className="p-3">Vencimento</th>}
                    {isHedge && <th className="p-3 text-right">Spot Usado</th>}
                    <th className="p-3">Prazo</th>
                    <th className="p-3">Data</th>
                    <th className="p-3">Dias</th>
                    <th className="p-3">Status</th>
                    {isHedge && <th className="p-3">Vendedor</th>}
                    <th className="p-3">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {travasFiltradas.map((t) => {
                    const dias = diasAberto(t.data_trava);
                    return (
                      <tr key={t.codigo} data-testid={`row-trava-${t.codigo}`} className="border-b border-[#0e2e4e] hover:bg-[#0B2D4D]/50">
                        <td className="p-3 font-mono-data font-semibold text-[#40B6A2]">
                          <span className="flex items-center gap-1">
                            {t.codigo}
                            <button type="button" onClick={() => handleCopyCodigo(t.codigo)} className="text-[#6b89a8] hover:text-white transition-colors" title="Copiar código">
                              {copiedCodigo === t.codigo ? <Check className="w-3.5 h-3.5 text-[#40B6A2]" /> : <Copy className="w-3.5 h-3.5" />}
                            </button>
                          </span>
                        </td>
                        <td className="p-3 truncate max-w-[180px]">{t.cliente}</td>
                        <td className="p-3 text-right font-mono-data">{fmt(t.volume_usd, 2)}</td>
                        <td className="p-3 text-right font-mono-data">
                          R$ {fmt(t.taxa_final)}
                          {t.taxa_csv != null && Math.abs(t.taxa_csv - t.taxa_final) >= 0.0001 && (
                            <div className="text-[10px] text-red-400 mt-0.5">
                              SYSFÉRTIL: {t.taxa_csv.toFixed(4).replace(".", ",")} {t.taxa_csv > t.taxa_final ? "▲" : "▼"}
                            </div>
                          )}
                        </td>
                        {isHedge && (
                          <td className="p-3">
                            {/* NULL = trava da regra de set/2026, em que a escolha
                                "com juros / sem juros" não existe. Precisa de
                                checagem explícita: `null` é falsy e cairia em
                                "Sem juros", rotulando errado toda trava nova. */}
                            {t.com_juros == null
                              ? <span className="text-[10px] font-mono-data text-[#6b89a8]" title="Regra de spread única — não se aplica">—</span>
                              : t.com_juros
                                ? <span className="text-[10px] font-semibold px-2 py-0.5 rounded-sm bg-yellow-500/15 text-yellow-400">Com juros</span>
                                : <span className="text-[10px] font-semibold px-2 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-400">Sem juros</span>}
                          </td>
                        )}
                        {isHedge && (
                          <td className="p-3 font-mono-data text-xs text-[#8aa9c9]">
                            {t.tipo_vencimento === "ddf"
                              ? `DDF · ${t.ddf_dias}d`
                              : t.data_vencimento
                                ? new Date(t.data_vencimento + "T00:00:00").toLocaleDateString("pt-BR")
                                : "—"}
                          </td>
                        )}
                        {isHedge && (
                          <td className="p-3 text-right font-mono-data text-[#8aa9c9]">
                            {fmt(t.spot)}
                          </td>
                        )}
                        <td className="p-3 font-mono-data">{t.prazo_dias}d</td>
                        <td className="p-3 font-mono-data text-xs text-[#8aa9c9]">{new Date(t.data_trava).toLocaleDateString("pt-BR")}</td>
                        <td className={`p-3 font-mono-data font-bold ${corDia(dias)}`}>{dias}d</td>
                        <td className="p-3">
                          {t.numero_pedido
                            ? <span className="text-[10px] font-mono-data font-semibold" style={{ color: "#40B6A2" }}>VINCULADO · {t.numero_pedido}</span>
                            : <StatusBadge status={t.status} />}
                        </td>
                        {isHedge && <td className="p-3 text-xs text-[#8aa9c9] truncate max-w-[150px]">{t.vendedor_nome}</td>}
                        <td className="p-3">
                          <div className="flex gap-1 flex-wrap items-center">
                            {!isHedge && (
                              <span
                                className="font-mono-data text-xs"
                                data-testid={`pedido-texto-${t.codigo}`}
                                style={{ color: t.numero_pedido ? "#40B6A2" : "#6b89a8" }}
                              >
                                {t.numero_pedido || "—"}
                              </span>
                            )}
                            {!isHedge && t.status !== "cancelada" && t.status !== "rejeitada" && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={t.status === "solicitacao_cancelamento"}
                                onClick={() => setCancelModal({ codigo: t.codigo, motivo: "" })}
                                data-testid={`btn-cancelar-${t.codigo}`}
                                className="h-7 px-2 border-red-600/40 text-red-500 hover:bg-red-600/10 hover:text-red-400 disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                Solicitar Cancelamento
                              </Button>
                            )}
                            {isHedge && t.status === "solicitacao_cancelamento" && (
                              <div className="flex gap-1">
                                <Button size="sm" onClick={() => setConfirmCancelModal({ codigo: t.codigo, cliente: t.cliente, motivo_cancelamento: t.motivo_cancelamento, tipo: "aprovar" })} data-testid={`btn-aprovar-cancel-${t.codigo}`} className="h-7 px-2 bg-[#40B6A2] text-black hover:bg-[#359a89]"><CheckCircle className="w-3 h-3" /></Button>
                                <Button size="sm" onClick={() => setConfirmCancelModal({ codigo: t.codigo, cliente: t.cliente, motivo_cancelamento: t.motivo_cancelamento, tipo: "rejeitar" })} data-testid={`btn-rejeitar-cancel-${t.codigo}`} className="h-7 px-2 bg-[#FF3B30] text-white hover:bg-red-700"><XCircle className="w-3 h-3" /></Button>
                              </div>
                            )}
                            {isHedge && t.status === "pendente_aprovacao_alcada" && (
                              <>
                                <Button size="sm" onClick={() => aprovar(t.codigo, "alcada")} data-testid={`btn-aprovar-alcada-${t.codigo}`} className="h-7 px-2 bg-[#40B6A2] text-black hover:bg-[#359a89]">Aprovar</Button>
                                <Button size="sm" onClick={() => rejeitar(t.codigo, "alcada")} data-testid={`btn-rejeitar-alcada-${t.codigo}`} className="h-7 px-2 bg-[#FF3B30] text-white hover:bg-red-700">Rejeitar</Button>
                              </>
                            )}
                            {isHedge && ["pendente_vinculacao", "pendente", "pendente_ptax"].includes(t.status) && (
                              <Button
                                size="sm"
                                onClick={() => setVincularModal({ codigo: t.codigo, numero_pedido: "" })}
                                data-testid={`btn-vincular-${t.codigo}`}
                                className="h-7 px-2 bg-[#40B6A2] text-black hover:bg-[#359a89]"
                              >
                                Vincular
                              </Button>
                            )}
                            {isHedge && t.tipo === "ptax" && t.status === "pendente" && (
                              <Button
                                size="sm"
                                onClick={() => setPtaxModal({ codigo: t.codigo, taxa: "" })}
                                data-testid={`btn-taxa-ptax-${t.codigo}`}
                                className="h-7 px-2 bg-[#0ea5e9] text-white hover:bg-[#0284c7]"
                              >
                                Lançar Taxa
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile: cards */}
            <div className="md:hidden space-y-3">
              {travasFiltradas.map((t) => (
                <TravaCard
                  key={t.codigo}
                  t={t}
                  isHedge={isHedge}
                  fmt={fmt}
                  diasAberto={diasAberto}
                  corDia={corDia}
                  expandedCard={expandedCard}
                  setExpandedCard={setExpandedCard}
                  {...acoes}
                />
              ))}
            </div>
            </>
            )}
          </>
        )}
      </div>

      <Dialog open={!!cancelModal} onOpenChange={(open) => !open && setCancelModal(null)}>
        <DialogContent className="bg-[#0B2D4D] border border-[#123a5e] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-semibold">
              <Warning weight="fill" className="w-5 h-5 text-red-500" />
              Solicitar Cancelamento
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#b8cde2] leading-relaxed">
              Deseja solicitar o cancelamento desta trava? A solicitação será enviada para aprovação do Hedge.
            </p>
            <div className="space-y-1">
              <label className="text-xs tracking-wider uppercase text-[#8aa9c9]">Motivo</label>
              <textarea
                rows={3}
                placeholder="Descreva o motivo..."
                value={cancelModal?.motivo || ""}
                onChange={(e) => setCancelModal((m) => ({ ...m, motivo: e.target.value }))}
                data-testid="textarea-motivo-cancelamento"
                className="w-full bg-transparent border border-[#123a5e] rounded-sm px-3 py-2 text-sm text-white placeholder-[#6b89a8] focus:outline-none focus:border-red-500/60 resize-none"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={() => setCancelModal(null)} data-testid="btn-modal-cancelar" className="border-[#123a5e] text-[#8aa9c9] hover:text-white">
              Cancelar
            </Button>
            <Button onClick={handleConfirmCancelar} disabled={!cancelModal?.motivo?.trim()} data-testid="btn-modal-confirmar" className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed">
              Confirmar Solicitação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Confirmar aprovação/rejeição de cancelamento — hedge */}
      <Dialog open={!!confirmCancelModal} onOpenChange={(open) => !open && setConfirmCancelModal(null)}>
        <DialogContent className="bg-[#0B2D4D] border border-[#123a5e] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-semibold">
              {confirmCancelModal?.tipo === "aprovar"
                ? <><CheckCircle weight="fill" className="w-5 h-5 text-[#40B6A2]" /> Aprovar Cancelamento</>
                : <><XCircle weight="fill" className="w-5 h-5 text-[#FF3B30]" /> Rejeitar Cancelamento</>
              }
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#8aa9c9]">Trava</span>
              <span className="font-mono-data text-white">{confirmCancelModal?.codigo}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#8aa9c9]">Cliente</span>
              <span className="text-white">{confirmCancelModal?.cliente}</span>
            </div>
            {confirmCancelModal?.motivo_cancelamento && (
              <div className="space-y-1">
                <span className="text-xs tracking-wider uppercase text-[#8aa9c9]">Motivo do vendedor</span>
                <p className="text-sm text-[#FFB800] leading-relaxed bg-[#0a2540] rounded-sm px-3 py-2 border border-[#123a5e]">
                  {confirmCancelModal.motivo_cancelamento}
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={() => setConfirmCancelModal(null)} className="border-[#123a5e] text-[#8aa9c9] hover:text-white">
              Voltar
            </Button>
            <Button
              onClick={async () => {
                const { codigo, tipo } = confirmCancelModal;
                setConfirmCancelModal(null);
                await (tipo === "aprovar" ? aprovar : rejeitar)(codigo, "cancel");
              }}
              className={confirmCancelModal?.tipo === "aprovar"
                ? "bg-[#40B6A2] text-black hover:bg-[#359a89]"
                : "bg-[#FF3B30] text-white hover:bg-red-700"
              }
            >
              {confirmCancelModal?.tipo === "aprovar" ? "Confirmar Aprovação" : "Confirmar Rejeição"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Vincular manual */}
      <Dialog open={!!vincularModal} onOpenChange={(open) => !open && setVincularModal(null)}>
        <DialogContent className="bg-[#0B2D4D] border border-[#123a5e] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-semibold">
              <CheckCircle weight="fill" className="w-5 h-5 text-[#40B6A2]" />
              Vincular trava <span className="font-mono-data text-[#40B6A2]">{vincularModal?.codigo}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#b8cde2] leading-relaxed">
              Informe o número do pedido do Sysfértil que será vinculado a esta trava.
            </p>
            <div className="space-y-1">
              <label className="text-xs tracking-wider uppercase text-[#8aa9c9]">Número do pedido</label>
              <input
                type="text"
                autoFocus
                placeholder="Ex: 19006"
                value={vincularModal?.numero_pedido || ""}
                onChange={(e) => setVincularModal((m) => ({ ...m, numero_pedido: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") handleConfirmVincular(); }}
                data-testid="input-vincular-numero-pedido"
                className="w-full bg-transparent border border-[#123a5e] rounded-sm px-3 py-2 text-sm font-mono-data text-white placeholder-[#6b89a8] focus:outline-none focus:border-[#40B6A2]/60"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={() => setVincularModal(null)} data-testid="btn-vincular-cancelar" className="border-[#123a5e] text-[#8aa9c9] hover:text-white">
              Cancelar
            </Button>
            <Button onClick={handleConfirmVincular} disabled={vincularLoading} data-testid="btn-vincular-confirmar" className="bg-[#40B6A2] text-black hover:bg-[#359a89] font-semibold">
              {vincularLoading ? "Vinculando..." : "Vincular"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Lançar Taxa PTAX */}
      <Dialog open={!!ptaxModal} onOpenChange={(open) => !open && setPtaxModal(null)}>
        <DialogContent className="bg-[#0B2D4D] border border-[#123a5e] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-semibold">
              <span className="text-[#0ea5e9]">$</span>
              Lançar Taxa PTAX — <span className="font-mono-data text-[#0ea5e9]">{ptaxModal?.codigo}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#b8cde2] leading-relaxed">
              Informe a taxa PTAX a ser registrada nesta trava. O status da trava não será alterado.
            </p>
            <div className="space-y-1">
              <label className="text-xs tracking-wider uppercase text-[#8aa9c9]">Taxa (R$)</label>
              <input
                type="text"
                autoFocus
                placeholder="Ex: 5,8742"
                value={ptaxModal?.taxa || ""}
                onChange={(e) => setPtaxModal((m) => ({ ...m, taxa: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") handleConfirmPtax(); }}
                data-testid="input-taxa-ptax"
                className="w-full bg-transparent border border-[#123a5e] rounded-sm px-3 py-2 text-sm font-mono-data text-white placeholder-[#6b89a8] focus:outline-none focus:border-[#0ea5e9]/60"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={() => setPtaxModal(null)} data-testid="btn-ptax-cancelar" className="border-[#123a5e] text-[#8aa9c9] hover:text-white">
              Cancelar
            </Button>
            <Button onClick={handleConfirmPtax} disabled={ptaxLoading} data-testid="btn-ptax-confirmar" className="bg-[#0ea5e9] text-white hover:bg-[#0284c7] font-semibold">
              {ptaxLoading ? "Salvando..." : "Lançar Taxa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Importar CSV Sysfértil */}
      <Dialog open={csvModal} onOpenChange={(open) => !open && setCsvModal(false)}>
        <DialogContent className="bg-[#0B2D4D] border border-[#123a5e] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-semibold">
              <FileArrowUp className="w-5 h-5 text-[#40B6A2]" />
              Importar CSV Sysfértil
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!csvResult ? (
              <>
                <p className="text-sm text-[#b8cde2]">
                  Selecione o arquivo CSV exportado do Sysfértil. O sistema vinculará automaticamente as travas encontradas na coluna <span className="font-mono-data text-white">Cod Trava</span>.
                </p>
                <div className="space-y-1.5">
                  <label className="text-xs tracking-wider uppercase text-[#8aa9c9]">Arquivo CSV</label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                    className="w-full text-sm text-[#b8cde2] file:mr-3 file:py-1.5 file:px-3 file:border file:border-[#123a5e] file:bg-[#061729] file:text-[#8aa9c9] file:text-xs file:rounded-sm cursor-pointer"
                  />
                </div>
                {csvFile && (
                  <p className="text-xs text-[#6b89a8] font-mono-data">{csvFile.name}</p>
                )}
              </>
            ) : (
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-1 border border-[#40B6A2]/30 bg-[#40B6A2]/5 rounded-sm py-3 text-center">
                    <p className="text-2xl font-bold font-mono-data text-[#40B6A2]">{csvResult.vinculadas}</p>
                    <p className="text-xs text-[#6b89a8] mt-1">Vinculadas</p>
                  </div>
                  <div className="flex-1 border border-[#FFB800]/30 bg-[#FFB800]/5 rounded-sm py-3 text-center">
                    <p className="text-2xl font-bold font-mono-data text-[#FFB800]">{csvResult.taxa_atualizada}</p>
                    <p className="text-xs text-[#6b89a8] mt-1">Taxa atualizada</p>
                  </div>
                  <div className="flex-1 border border-[#123a5e] rounded-sm py-3 text-center">
                    <p className="text-2xl font-bold font-mono-data text-[#8aa9c9]">{csvResult.ignoradas}</p>
                    <p className="text-xs text-[#6b89a8] mt-1">Ignoradas</p>
                  </div>
                </div>
                {csvResult.detalhes.filter(d => d.status === "taxa_atualizada").length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs uppercase tracking-wider text-[#8aa9c9]">Taxa atualizada</p>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {csvResult.detalhes.filter(d => d.status === "taxa_atualizada").map(d => {
                        const delta = (d.taxa_csv != null && d.taxa_sistema != null) ? d.taxa_csv - d.taxa_sistema : null;
                        const deltaAbs = delta != null ? Math.abs(delta) : null;
                        const deltaColor = deltaAbs == null ? "#6b89a8" : deltaAbs < 0.01 ? "#40B6A2" : deltaAbs < 0.05 ? "#FFB800" : "#FF3B30";
                        const fmtTaxa = (v) => v?.toFixed(4).replace(".", ",");
                        return (
                          <div key={d.codigo} className="px-2 py-1.5 bg-[#FFB800]/5 border border-[#FFB800]/20 rounded-sm space-y-0.5">
                            <div className="flex items-center justify-between text-xs">
                              <span className="font-mono-data text-[#FFB800]">{d.codigo}</span>
                              <span className="text-[#6b89a8]">já vinculada · taxa CSV atualizada</span>
                            </div>
                            {d.taxa_csv != null && (
                              <div className="flex items-center gap-1.5 text-[10px] font-mono-data text-[#8aa9c9]">
                                <span>CSV {fmtTaxa(d.taxa_csv)}</span>
                                <span className="text-[#6b89a8]">·</span>
                                <span>Sistema {fmtTaxa(d.taxa_sistema)}</span>
                                {delta != null && (
                                  <>
                                    <span className="text-[#6b89a8]">·</span>
                                    <span style={{ color: deltaColor }}>Δ {delta >= 0 ? "+" : ""}{fmtTaxa(delta)}</span>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {csvResult.detalhes.filter(d => d.status === "vinculada").length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs uppercase tracking-wider text-[#8aa9c9]">Travas vinculadas</p>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {csvResult.detalhes.filter(d => d.status === "vinculada").map(d => {
                        const delta = (d.taxa_csv != null && d.taxa_sistema != null) ? d.taxa_csv - d.taxa_sistema : null;
                        const deltaAbs = delta != null ? Math.abs(delta) : null;
                        const deltaColor = deltaAbs == null ? "#6b89a8" : deltaAbs < 0.01 ? "#40B6A2" : deltaAbs < 0.05 ? "#FFB800" : "#FF3B30";
                        const fmtTaxa = (v) => v?.toFixed(4).replace(".", ",");
                        return (
                          <div key={d.codigo} className="px-2 py-1.5 bg-[#40B6A2]/5 border border-[#40B6A2]/20 rounded-sm space-y-0.5">
                            <div className="flex items-center justify-between text-xs">
                              <span className="font-mono-data text-[#40B6A2]">{d.codigo}</span>
                              <span className="text-[#6b89a8] font-mono-data">Ped. {d.pedido || "—"}</span>
                            </div>
                            {d.taxa_csv != null && (
                              <div className="flex items-center gap-1.5 text-[10px] font-mono-data text-[#8aa9c9]">
                                <span>CSV {fmtTaxa(d.taxa_csv)}</span>
                                <span className="text-[#6b89a8]">·</span>
                                <span>Sistema {fmtTaxa(d.taxa_sistema)}</span>
                                {delta != null && (
                                  <>
                                    <span className="text-[#6b89a8]">·</span>
                                    <span style={{ color: deltaColor }}>Δ {delta >= 0 ? "+" : ""}{fmtTaxa(delta)}</span>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {csvResult.detalhes.filter(d => d.status === "ignorada").length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs uppercase tracking-wider text-[#8aa9c9]">Ignoradas</p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {csvResult.detalhes.filter(d => d.status === "ignorada").map((d, i) => (
                        <div key={i} className="flex items-center justify-between text-xs px-2 py-1.5 bg-[#123a5e]/30 rounded-sm">
                          <span className="font-mono-data text-[#8aa9c9]">{d.codigo}</span>
                          <span className="text-[#6b89a8]">{d.motivo}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 pt-2">
            {!csvResult ? (
              <>
                <Button variant="outline" onClick={() => setCsvModal(false)} className="border-[#123a5e] text-[#8aa9c9] hover:text-white">
                  Cancelar
                </Button>
                <Button onClick={handleVincularCsv} disabled={!csvFile || csvLoading} className="bg-[#40B6A2] text-black hover:bg-[#359a89] font-semibold">
                  {csvLoading ? "Processando..." : "Processar"}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => { setCsvFile(null); setCsvResult(null); }} className="border-[#123a5e] text-[#8aa9c9] hover:text-white">
                  Novo arquivo
                </Button>
                <Button onClick={() => setCsvModal(false)} className="bg-[#40B6A2] text-black hover:bg-[#359a89] font-semibold">
                  Fechar
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

const STATUS_BORDER_COLOR = {
  vinculada: "#40B6A2",
  pendente_vinculacao: "#FFB800",
  pendente_aprovacao_alcada: "#FFB800",
  solicitacao_cancelamento: "#FF3B30",
  cancelada: "#FF3B30",
  rejeitada: "#FF3B30",
};

function TravaCard({ t, isHedge, fmt, diasAberto, corDia, setCancelModal, aprovar, rejeitar, expandedCard, setExpandedCard, setVincularModal, setPtaxModal, setConfirmCancelModal }) {
  const dias = diasAberto(t.data_trava);
  const margem = (t.spread_aplicado ?? 0) * (t.volume_usd ?? 0);
  const borderColor = STATUS_BORDER_COLOR[t.status] ?? "#123a5e";
  const isExpanded = expandedCard === t.codigo;

  const toggle = () => setExpandedCard(isExpanded ? null : t.codigo);

  const [copiedCard, setCopiedCard] = useState(false);
  const handleCopyCard = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(t.codigo).then(() => {
      setCopiedCard(true);
      setTimeout(() => setCopiedCard(false), 1500);
    });
  };

  return (
    <div
      data-testid={`card-trava-${t.codigo}`}
      style={{ borderLeftColor: borderColor }}
      className="bg-[#0B2D4D] border border-[#123a5e] border-l-4 overflow-hidden"
    >
      {/* Cabeçalho — sempre visível, clicável */}
      <div
        onClick={toggle}
        className="w-full flex items-center justify-between px-4 h-14 cursor-pointer select-none"
        data-testid={`card-toggle-${t.codigo}`}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && toggle()}
      >
        <div className="flex items-center gap-2">
          <span className="font-mono-data text-xl font-bold text-[#40B6A2] tracking-wider">{t.codigo}</span>
          <button type="button" onClick={handleCopyCard} className="text-[#6b89a8] hover:text-white transition-colors p-1" title="Copiar código">
            {copiedCard ? <Check className="w-4 h-4 text-[#40B6A2]" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={t.status} />
          <CaretDown
            className={`w-4 h-4 text-[#8aa9c9] transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
          />
        </div>
      </div>

      {/* Conteúdo expansível */}
      <div
        className={`grid transition-[grid-template-rows] duration-200 ${isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          <div className="px-4 pb-4 space-y-4 border-t border-[#123a5e] pt-4">
            {/* Campos */}
            <div className="space-y-4">
              {/* Cliente — largura total */}
              <div>
                <p className="text-[11px] uppercase tracking-[0.15em] text-[#8aa9c9]">Cliente</p>
                <p className="text-white text-[15px] font-medium mt-1 leading-snug">{t.cliente}</p>
              </div>

              {/* Volume USD / Taxa — 2 colunas */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#8aa9c9]">Volume USD</p>
                  <p className="text-white text-[15px] font-mono-data mt-1">{fmt(t.volume_usd, 2)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#8aa9c9]">Taxa</p>
                  <p className="text-white text-[15px] font-mono-data mt-1">R$ {fmt(t.taxa_final)}</p>
                </div>
              </div>

              {/* Spot / Prazo — 2 colunas */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#8aa9c9]">Spot</p>
                  <p className="text-white text-[15px] font-mono-data mt-1">R$ {fmt(t.spot)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#8aa9c9]">Prazo</p>
                  <p className="text-white text-[15px] font-mono-data mt-1">{t.prazo_dias} dias</p>
                </div>
              </div>

              {/* Margem BRL — largura total */}
              <div>
                <p className="text-[11px] uppercase tracking-[0.15em] text-[#8aa9c9]">Margem BRL</p>
                <p className="text-white text-[15px] font-mono-data mt-1">R$ {fmt(margem, 2)}</p>
              </div>

              {/* Data / Dias Aberto — 2 colunas */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#8aa9c9]">Data</p>
                  <p className="text-[#8aa9c9] text-sm font-mono-data mt-1">{new Date(t.data_trava).toLocaleDateString("pt-BR")}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#8aa9c9]">Dias Aberto</p>
                  <p className={`text-[15px] font-mono-data font-bold mt-1 ${corDia(dias)}`}>{dias}d</p>
                </div>
              </div>

              {/* Vendedor — apenas perfil Hedge */}
              {isHedge && (
                <div>
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#8aa9c9]">Vendedor</p>
                  <p className="text-[#8aa9c9] text-sm mt-1">{t.vendedor_nome}</p>
                </div>
              )}
            </div>

            {/* Ações */}
            <div className="space-y-2 pt-1">
              {/* Pedido — somente leitura para vendedor */}
              {!isHedge && (
                <div>
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#8aa9c9]">Pedido</p>
                  <p
                    className="text-[15px] font-mono-data mt-1"
                    data-testid={`pedido-texto-${t.codigo}`}
                    style={{ color: t.numero_pedido ? "#40B6A2" : "#6b89a8" }}
                  >
                    {t.numero_pedido || "—"}
                  </p>
                </div>
              )}

              {/* Solicitar Cancelamento — vendedor */}
              {!isHedge && t.status !== "cancelada" && t.status !== "rejeitada" && (
                <Button
                  variant="outline"
                  disabled={t.status === "solicitacao_cancelamento"}
                  onClick={() => setCancelModal({ codigo: t.codigo, motivo: "" })}
                  data-testid={`btn-cancelar-${t.codigo}`}
                  className="w-full h-10 border-red-600/40 text-red-500 hover:bg-red-600/10 hover:text-red-400 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Solicitar Cancelamento
                </Button>
              )}

              {/* Aprovar / Rejeitar cancelamento — hedge */}
              {isHedge && t.status === "solicitacao_cancelamento" && (
                <div className="flex gap-2">
                  <Button
                    onClick={() => setConfirmCancelModal({ codigo: t.codigo, cliente: t.cliente, motivo_cancelamento: t.motivo_cancelamento, tipo: "aprovar" })}
                    data-testid={`btn-aprovar-cancel-${t.codigo}`}
                    className="flex-1 h-10 bg-[#40B6A2] text-black hover:bg-[#359a89]"
                  >
                    <CheckCircle className="w-4 h-4 mr-1.5" /> Aprovar
                  </Button>
                  <Button
                    onClick={() => setConfirmCancelModal({ codigo: t.codigo, cliente: t.cliente, motivo_cancelamento: t.motivo_cancelamento, tipo: "rejeitar" })}
                    data-testid={`btn-rejeitar-cancel-${t.codigo}`}
                    className="flex-1 h-10 bg-[#FF3B30] text-white hover:bg-red-700"
                  >
                    <XCircle className="w-4 h-4 mr-1.5" /> Rejeitar
                  </Button>
                </div>
              )}

              {/* Aprovar / Rejeitar alçada — hedge */}
              {isHedge && t.status === "pendente_aprovacao_alcada" && (
                <div className="flex gap-2">
                  <Button
                    onClick={() => aprovar(t.codigo, "alcada")}
                    data-testid={`btn-aprovar-alcada-${t.codigo}`}
                    className="flex-1 h-10 bg-[#40B6A2] text-black hover:bg-[#359a89]"
                  >
                    Aprovar
                  </Button>
                  <Button
                    onClick={() => rejeitar(t.codigo, "alcada")}
                    data-testid={`btn-rejeitar-alcada-${t.codigo}`}
                    className="flex-1 h-10 bg-[#FF3B30] text-white hover:bg-red-700"
                  >
                    Rejeitar
                  </Button>
                </div>
              )}

              {/* Vincular manual — hedge */}
              {isHedge && ["pendente_vinculacao", "pendente", "pendente_ptax"].includes(t.status) && (
                <Button
                  onClick={() => setVincularModal({ codigo: t.codigo, numero_pedido: "" })}
                  data-testid={`btn-vincular-${t.codigo}`}
                  className="w-full h-10 bg-[#40B6A2] text-black hover:bg-[#359a89]"
                >
                  Vincular ao pedido
                </Button>
              )}
              {/* Lançar Taxa PTAX — hedge */}
              {isHedge && t.tipo === "ptax" && t.status === "pendente" && (
                <Button
                  onClick={() => setPtaxModal({ codigo: t.codigo, taxa: "" })}
                  data-testid={`btn-taxa-ptax-${t.codigo}`}
                  className="w-full h-10 bg-[#0ea5e9] text-white hover:bg-[#0284c7]"
                >
                  Lançar Taxa
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterSelect({ value, onChange, options, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="bg-[#061729] border border-[#123a5e] text-sm text-white px-3 py-1.5 rounded-sm focus:outline-none focus:border-[#40B6A2]/50 cursor-pointer min-w-[150px]"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} className="bg-[#061729]">{o.label}</option>
      ))}
    </select>
  );
}


function StatusBadge({ status }) {
  const map = {
    pendente_vinculacao: { label: "Pendente", color: "bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/30" },
    pendente_aprovacao_alcada: { label: "Alçada", color: "bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/30" },
    vinculada: { label: "Vinculada", color: "bg-[#40B6A2]/10 text-[#40B6A2] border-[#40B6A2]/30" },
    cancelada: { label: "Cancelada", color: "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/30" },
    rejeitada: { label: "Rejeitada", color: "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/30" },
    solicitacao_cancelamento: { label: "Canc. Pend.", color: "bg-orange-500/10 text-orange-500 border-orange-500/30" },
  };
  const c = map[status] || { label: status, color: "border-[#1a4773]" };
  return <Badge className={`${c.color} border font-mono-data text-[10px] tracking-wider uppercase`}>{c.label}</Badge>;
}
